'use client'

import React from 'react'
import { Box, Flex, Grid, GridCol, Stack, Text, Title } from '@mantine/core'
import { IconCircleCheck } from '@tabler/icons-react'
import type { Sku } from '@/types/interfaces/sku'
import type { SkuComponentConfigMap } from '@/types/enums/sku-component-types'
import type { VisitOptionsData } from '@/components/Refresh/SkuPages/SkuRefreshRenderer'
import { ServiceItemCardBadge } from '@/components/ServiceItemCardBadge'
import { SkuDetailCta } from '@/components/SkuDetailCta'
import { ServiceItemCategory } from '@/types/enums/ServiceItem'
import { VisitOptionsPreview } from '@/components/VisitOptionsPreview'
import { LabPanelPricing } from '@/components/LabPanelPricing'
import { GreenSection } from '@/components/Refresh/CategoryPages/CategoryHeader/GreenSection'
import { SkuRefreshHeroImage } from '@/components/Refresh/SkuPages/SkuHeader/SkuRefreshHeroImage'
import { getSkuRefreshDefaultBulletTexts } from '@/components/Refresh/SkuPages/SkuHeader/skuRefreshDefaultBullets'
import { getSkuRefreshDefaultDescription } from '@/components/Refresh/SkuPages/SkuHeader/skuRefreshDefaultDescription'
import { parseHtml } from '@/lib/parseHtml'
import { useMediaQuery } from '@mantine/hooks'

type SkuRefreshHeaderConfig = SkuComponentConfigMap['skuRefreshHeader']

export function SkuRefreshHeader({
    sku,
    config,
    visitOptionsData,
    inPersonAvailable,
    hideMobileInlineCta = false,
}: {
    sku: Sku
    config: SkuRefreshHeaderConfig
    visitOptionsData: VisitOptionsData | null
    inPersonAvailable: boolean
    hideMobileInlineCta?: boolean
}) {
    const response = visitOptionsData?.response ?? null
    const userHomeTimezone = visitOptionsData?.userHomeTimezone ?? null
    const isDesktop = useMediaQuery('(min-width: 62em)')
    const isLabPanels = sku.category === 'lab'
    const wellnessLabSku = sku.id === '500001'

    const displayTitle = config.title ?? sku.name
    const displayDescription = config.description ?? getSkuRefreshDefaultDescription(sku)
    const bullets = config.bulletPoints ?? getSkuRefreshDefaultBulletTexts(sku)

    const sharedBulletTextProps = {
        c: 'gray.6',
        fz: 16,
        lh: isLabPanels ? '21px' : '125%',
    } as const

    return (
        <Box
            w="100%"
            maw="1440px"
            mx="auto"
            bg={{ base: 'transparent', md: 'var(--Background-bg-card, #FAF7F5)' }}
            bd={{ base: 'none', md: '1px solid rgba(209, 208, 207, 0.5)' }}
            style={{
                minWidth: 0,
                overflow: 'hidden',
                borderRadius: isDesktop ? 24 : 0,
                boxShadow: isDesktop
                    ? '0 1px 1px 0 rgba(64, 63, 62, 0.05), 0 2px 7px 0 rgba(64, 63, 62, 0.05)'
                    : 'none',
            }}
        >
            <Grid gutter="xl" px={{ base: 0, md: 24 }} py={{ base: 16, sm: 56 }} align="flex-start">
                <SkuRefreshHeroImage
                    sku={sku}
                    skuCategory={sku?.category}
                    overrideImageSrc={config.imageSrc}
                    overrideImageAlt={config.imageAlt}
                />
                <GridCol span={{ base: 12, md: 7, lg: 6 }} style={{ minWidth: 0 }}>
                    <Flex direction="column" gap={16} w="100%">
                        <Flex direction="column" gap={{ base: 12, md: 16 }}>
                            <ServiceItemCardBadge
                                category={sku.category as ServiceItemCategory}
                                label={inPersonAvailable ? 'In-person visit' : undefined}
                            />
                            <Text
                                component="h1"
                                fz={{ base: wellnessLabSku ? 36 : 36, md: 48 }}
                                fw={{ base: wellnessLabSku ? 500 : 'var(--weight-display-large, 400)', md: 500 }}
                                lh={{ base: wellnessLabSku ? '50px' : '100%', md: 1.1 }}
                                c="gray.9"
                                lts={{ base: '-2%', md: '-1px' }}
                            >
                                {displayTitle}
                            </Text>
                        </Flex>

                        {displayDescription ? (
                            <Box>
                                <Text
                                    fw={isLabPanels ? 400 : 'var(--weight-text, 500)'}
                                    fz={16}
                                    lh="140%"
                                    c={'var(--gm-foreground-fg-secondary, #4F4E4D)'}
                                    style={{ wordBreak: 'keep-all' }}
                                    component="div"
                                >
                                    {parseHtml(displayDescription)}
                                </Text>
                            </Box>
                        ) : null}

                        <Stack gap={12} mb={{ sm: 'auto' }}>
                            {bullets.map((text) => (
                                <Flex key={text} align="flex-start" gap={8} style={{ alignSelf: 'stretch' }}>
                                    <IconCircleCheck
                                        size={20}
                                        color="#087442"
                                        stroke={2}
                                        style={{ flexShrink: 0, marginTop: 2 }}
                                    />
                                    <Text {...sharedBulletTextProps}>{text}</Text>
                                </Flex>
                            ))}
                        </Stack>
                        <Box
                            visibleFrom={hideMobileInlineCta ? 'md' : undefined}
                            mb={{ base: 12, sm: 24 }}
                            mt={{ base: 12, sm: 12 }}
                            style={{ alignSelf: 'stretch' }}
                            maw={{ base: '100%', sm: 200 }}
                        >
                            <SkuDetailCta sku={sku} skuPageVariant="refresh" />
                        </Box>

                        <Box style={{ alignSelf: 'stretch' }}>
                            {isLabPanels ? (
                                <LabPanelPricing
                                    skuId={sku.id}
                                    wellnessSku={wellnessLabSku}
                                    fullWidth
                                    skuPageVariant="refresh"
                                />
                            ) : (
                                <VisitOptionsPreview
                                    sku={sku}
                                    response={response}
                                    userHomeTimezone={userHomeTimezone}
                                    theme="tight"
                                    skuPageVariant="refresh"
                                    inPersonAvailable={inPersonAvailable}
                                    pageLocation="sku_refresh_header"
                                />
                            )}
                        </Box>
                    </Flex>
                </GridCol>
            </Grid>

            <Box w="100%" px={{ base: 0, sm: 24 }} pb={{ base: 12, sm: 24 }} mt={{ base: 12, sm: 'auto' }}>
                <GreenSection items={config.greenSectionItems} />
            </Box>
        </Box>
    )
}
